import * as React from 'react';
import { inject, observer } from 'mobx-react';
import Filters from '../../components/Filters';
import ProductList from '../../components/ProductList';
import Spinner from '../../components/Spinner';
import { Product } from '../../models/interfaces';
import {
  PRODUCT_STORE,
  CART_STORE,
  COMPANY_STORE,
  ORDER_FORM_STORE,
  ORDER_TYPES_STORE,
  CUSTOMER_STORE,
  MODAL_STORE,
  FILTER_STORE,
} from '../../constants/constants';
import { CartStore } from '../../stores/CartStore';
import { CompanyStore } from '../../stores/CompanyStore';
import { OrderFormStore } from '../../stores/OrderFormStore';
import { OrderTypesStore } from '../../stores/OrderTypesStore';
import { CustomerStore } from '../../stores/CustomerStore';
import ModalStore from '../../stores/ModalStore';
import ProductStore from '../../stores/ProductStore';
import FilterStore from '../../stores/FilterStore';

interface ProductViewState {
  showPictures: boolean;
  largeProducts: boolean;
  filterState: boolean;
  selectedCompany: string;
  showItemNo: boolean;
  showProductWeightInput: boolean;
}

interface ProductViewProps {
  CartStore: CartStore;
  ProductStore: ProductStore;
  CompanyStore: CompanyStore;
  OrderFormStore: OrderFormStore;
  OrderTypesStore: OrderTypesStore;
  CustomerStore: CustomerStore;
  ModalStore: ModalStore;
  FilterStore: FilterStore;
}

@inject(
  PRODUCT_STORE,
  CART_STORE,
  COMPANY_STORE,
  ORDER_FORM_STORE,
  ORDER_TYPES_STORE,
  CUSTOMER_STORE,
  MODAL_STORE,
  FILTER_STORE
)
@observer
class ProductView extends React.Component<ProductViewProps, ProductViewState> {
  constructor(props: ProductViewProps) {
    super(props);
    this.state = {
      showPictures: false,
      largeProducts: false,
      filterState: false,
      selectedCompany: '',
      showItemNo: false,
      showProductWeightInput: true,
    };
  }

  componentDidMount() {
    let itemNoString = localStorage.getItem('showItemNo');
    let showProductWeight = localStorage.getItem('showProductWeightInput');
    const showProductImage = localStorage.getItem('showProductImage');

    if (showProductImage) {
      this.setState({
        showPictures: JSON.parse(showProductImage),
      });
    }

    if (itemNoString) {
      this.setState({
        showItemNo: JSON.parse(itemNoString),
      });
    }

    if (showProductWeight) {
      this.setState({
        showProductWeightInput: JSON.parse(showProductWeight),
      });
    }

    if (this.props[COMPANY_STORE].selectedCompany) {
      this.setState({
        selectedCompany: this.props[COMPANY_STORE].selectedCompany,
      });
    }

    this.props.ProductStore.textFilter = '';
    this.props.ProductStore.brandFilter = '';
  }

  layoutChange(change: string) {
    if (change === 'pictures') {
      localStorage.setItem(
        'showProductImage',
        JSON.stringify(!this.state.showPictures)
      );
      this.setState({ showPictures: !this.state.showPictures });
    } else {
      this.setState({ largeProducts: !this.state.largeProducts });
    }
  }

  changeFilterState() {
    this.setState({ filterState: !this.state.filterState });
  }

  getFilterState(additionalClass: string) {
    let base = '';

    if (additionalClass === 'fixed') {
      base = 'section section--filters ';
    }

    if (additionalClass === 'active') {
      base = 'filter-button mobile ';
    }

    base += this.state.filterState ? additionalClass : '';

    return base;
  }

  isCompanySelected(company: string, selectedCompany: string) {
    if (company === selectedCompany) {
      return 'action active';
    }
    return 'action';
  }

  onSelectCompany(company: string) {
    if (this.props[COMPANY_STORE].selectedCompany !== company) {
      this.setState({ selectedCompany: company });
      this.props[MODAL_STORE].data = company;
      this.props[MODAL_STORE].setTrigger('companyChange');
      this.props[MODAL_STORE].setTitle(
        <h2>Haluatko varmasti vaihtaa yhtiötä? tämä tyhjentää ostoskorin!</h2>
      );
      this.props[MODAL_STORE].openModal();
    }
  }

  changeCompany() {
    const company = this.state.selectedCompany;
    this.props[COMPANY_STORE].setCompany(company);

    this.props[CART_STORE].clearStore();
    this.props[ORDER_FORM_STORE].clearStore();

    this.props[PRODUCT_STORE].fetchProducts();
    this.props[ORDER_TYPES_STORE].getOrderTypes();
    this.props[CUSTOMER_STORE].fetchCustomers();
    this.props[FILTER_STORE].fetchFilters();
  }

  toggleItemnNo() {
    localStorage.setItem('showItemNo', JSON.stringify(!this.state.showItemNo));
    this.setState({
      showItemNo: !this.state.showItemNo,
    });
  }

  toggleShowProductWeightInput() {
    localStorage.setItem(
      'showProductWeightInput',
      JSON.stringify(!this.state.showProductWeightInput)
    );
    this.setState({
      showProductWeightInput: !this.state.showProductWeightInput,
    });
  }

  initializeLazyLoading() {
    // Should be enough of debounce for everything to load.
    setTimeout(function () {
      if ('IntersectionObserver' in window) {
        // Lazy load images
        const lazyImages = [].slice.call(
          document.querySelectorAll('.product__image.lazy img')
        );
        let lazyImageObserver = new IntersectionObserver(function (
          entries: any
        ) {
          entries.forEach(function (entry: any) {
            // Do these if the target intersects with the root
            if (entry.isIntersecting) {
              var lazyImage = entry.target;
              lazyImage.src = lazyImage.dataset.src;
              lazyImageObserver.unobserve(lazyImage);
            }
          });
        });

        lazyImages.forEach(function (lazyImage: Element) {
          lazyImageObserver.observe(lazyImage);
        });
      } else {
        // IE11 fix.
        var lazyImages = document.querySelectorAll('.product__image.lazy img');
        for (var i = 0; i < lazyImages.length; i++) {
          var lazyImage: any = lazyImages[i];
          lazyImage.src = lazyImage.dataset.src;
        }
      }
    }, 500);
  }

  render() {
    const cartStore = this.props[CART_STORE];
    const productStore = this.props[PRODUCT_STORE];
    // const allProducts = productStore.allProducts as Array<Product>;
    const filteredProducts = productStore.filteredProducts as Array<Product>;
    const selectedCompany = this.props[COMPANY_STORE].selectedCompany;
    const loadingProducts = this.props[PRODUCT_STORE].requesting;
    const totalWeight = productStore.weight;

    const marketingFilters = [
      {
        name: 'Markkinointimateriaali',
        id: 'MARKK',
      },
      {
        name: 'Kampanja',
        id: 'KAMP',
      },
      {
        name: 'Messu',
        id: 'MESS',
      },
      {
        name: 'Toimisto',
        id: 'TOIM',
      },
    ];

    // If searchQuery is less than 2 words, don't do anything.
    const changeTextFilter = (searchQuery: string) => {
      productStore.textFilter = searchQuery.length > 2 ? searchQuery : '';

      this.initializeLazyLoading();
    };

    if (!loadingProducts) {
      // https://dev.to/ekafyi/lazy-loading-images-with-vanilla-javascript-2fbj
      // Do this only if IntersectionObserver is supported
      this.initializeLazyLoading();
    }

    const changeBrandFilter = (brand: string) => {
      productStore.brandFilter = brand;
      this.initializeLazyLoading();
    };

    const changeProductFilter = () => {
      productStore.productFilter = !productStore.productFilter;
      this.initializeLazyLoading();
    };

    const changeMarketingMaterialFilter = (value: string) => {
      productStore.marketingMaterialFilter = value;
      this.initializeLazyLoading();
    };

    const getBrands = () => {
      let brands: Array<string> = [];
      brands = this.props[FILTER_STORE].brandFilters;

      return brands.sort((a, b) => {
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      });
    };

    const addProduct = (product: Product, amount: number) => {
      cartStore.addProduct(product, amount, selectedCompany);
    };

    return (
      <div className="section--product-view">
        <div className={this.getFilterState('fixed')}>
          <Filters
            textFilter={(searchQuery) => changeTextFilter(searchQuery)}
            brandFilter={(brand) => changeBrandFilter(brand)}
            marketingMaterialFilter={(value) =>
              changeMarketingMaterialFilter(value)
            }
            productsFilter={(value) => changeProductFilter()}
            marketingMaterialFilters={marketingFilters}
            brands={getBrands()}
            company={selectedCompany}
          />
        </div>
        <div className="section section--layout-filters">
          <div className="condensed filters-company-wrapper">
            <div className="layout-filters">
              <div
                className={this.state.showPictures ? 'action active' : 'action'}
                onClick={() => this.layoutChange('pictures')}
              >
                Näytä kuvat
              </div>
              <div
                className={
                  this.state.largeProducts ? 'action active' : 'action'
                }
                onClick={() => this.layoutChange('large')}
              >
                Näytä vierekkäin
              </div>
              <div
                className={this.state.showItemNo ? 'action active' : 'action'}
                onClick={() => this.toggleItemnNo()}
              >
                Näytä Tamro ID
              </div>
              <div
                className={
                  this.state.showProductWeightInput ? 'action active' : 'action'
                }
                onClick={() => this.toggleShowProductWeightInput()}
              >
                Näytä paino
              </div>
            </div>
            <div className="company-selector">
              <div
                className={this.isCompanySelected('verman', selectedCompany)}
                onClick={() => this.onSelectCompany('verman')}
              >
                Verman
              </div>
              <div
                className={this.isCompanySelected('bioteekki', selectedCompany)}
                onClick={() => this.onSelectCompany('bioteekki')}
              >
                Bioteekki
              </div>
            </div>
          </div>
        </div>
        <div className="section section--product-list">
          {loadingProducts ? <Spinner /> : null}
          <ProductList
            totalWeight={totalWeight}
            products={filteredProducts}
            addProduct={(product: Product, amount: number) =>
              addProduct(product, amount)
            }
            layout={{
              showPictures: this.state.showPictures,
              largeProducts: this.state.largeProducts,
              showItemNo: this.state.showItemNo,
              showWeightInput: this.state.showProductWeightInput,
            }}
          />
        </div>
        <div
          className={this.getFilterState('active')}
          onClick={() => this.changeFilterState()}
        >
          Suodata »
        </div>
      </div>
    );
  }
}

export default ProductView;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/product-view/ProductView.tsx